import { gsap, resetSlide } from "../core/gsap";
import { responsiveMediaAbove } from "../../settings/responsive";

const SELECTOR_SECTION_CONTAINER = '[data-video-animation-slides]';

const CONFIG = {
  INWARD_MULTIPLIER: responsiveMediaAbove('medium') ? 0.4 : 0.30,
  OUTWARD_MULTIPLIER: responsiveMediaAbove('medium') ? 1 : 0.8,
  ANIMATION_DURATION: 1,
  ANIMATION_DELAY: 1,
  TEXT_VIDEO_VISIBILITY_DURATION: 5
}

// Configuration for final image positions in OUTWARD animation
// You can easily adjust these values without touching the animation logic.
//
// Position Types:
// - leftPct/rightPct: Horizontal position as percentage of container width
// - moveY: Static pixel value to add to current Y position (positive = down, negative = up)
// - widthPx: Final width of the image in pixels
//
// For vertical positioning (mobile):
// - moveY: Pixels to move from current position (e.g., moveY: 50 moves 50px down, moveY: -30 moves 30px up)
// - This is independent of screen height and container size
const OUTWARD_TARGETS = {
  desktop: {
    "top-left":    { leftPct: 5,   topPct: 0, widthPx: 340 },
    "centre-left": { leftPct: 6,  topPct: 43, widthPx: 420 },
    "bottom-left": { leftPct: 2,   bottomPct: -2, widthPx: 209 },
    "top-right":   { rightPct: 5, topPct: 0, widthPx: 282 },
    "centre-right":{ rightPct: 14, topPct: 25, widthPx: 291 },
    "bottom-right":{ rightPct: 5, bottomPct: 0, widthPx: 382 },
  },
  mobile: {
    // Define movement from current position using static pixel values
    // These values are added to the element's current position
    "top-left":    { leftPct: 7,   moveY: -115,  widthPx: 164 },    // Move 115px up from current position
    "top-right":   { rightPct: 7,  moveY: -120,   widthPx: 136 },    // Move 120px up from current position
    "bottom-left": { leftPct: 11,  moveY: 90,  widthPx: 157 },    // Move 80px up from current position
    "bottom-right":{ rightPct: 11, moveY: 90,  widthPx: 126 }     // Move 90px up from current position
  }
};

const getElement = <T extends HTMLElement>(selector: string, parent: HTMLElement): T | null => parent.querySelector(selector) as T | null;
const getElements = (selector: string, parent: HTMLElement) => parent.querySelectorAll(selector);
const slideToNext = (timeline: gsap.core.Timeline, slide: HTMLElement) => {
  timeline.call(() => {
    slide.closest(SELECTOR_SECTION_CONTAINER)?.dispatchEvent(
      new CustomEvent("slideNext")
    );
  });
}

export function animateTextInward(slide: HTMLElement, isLastSlide: boolean) {
  resetSlide(slide);
  const tl = gsap.timeline();

  const textContainer = getElement('[data-slide-text-container]', slide);
  const subtitle = getElement('[data-slide-subtitle]', slide);
  const title = getElement('[data-slide-title]', slide);
  const description = getElement('[data-slide-description]', slide);
  const images = getElements('[data-slide-anim-img]', slide);
  const featuredImage = getElements('[data-slide-anim-img-featured]', slide);

  // Animate title and description in at the same time
  tl.to([title, description, subtitle], { opacity: 1, duration: CONFIG.ANIMATION_DURATION, delay: 0.5, ease: "power2.out" }, "<");

  // Move images closer to the center individually
  const initialPositions = new Map<number, { x: number; y: number }>();

  // Animate images: x, y, and opacity
  tl.to(images, {
    x: (i, el) => {
      const bounds = el.getBoundingClientRect();
      const parentBounds = slide.getBoundingClientRect();
      const centerX = parentBounds.left + parentBounds.width / 2;
      const x = (centerX - (bounds.left + bounds.width / 2)) * CONFIG.INWARD_MULTIPLIER;
      initialPositions.set(i, { x, y: 0 }); // y will be set below
      return x;
    },
    y: (i, el) => {
      const bounds = el.getBoundingClientRect();
      const parentBounds = slide.getBoundingClientRect();
      const centerY = parentBounds.top + parentBounds.height / 2;
      const y = (centerY - (bounds.top + bounds.height / 2)) * CONFIG.INWARD_MULTIPLIER;
      const pos = initialPositions.get(i);
      if (pos) {
        pos.y = y;
        initialPositions.set(i, pos);
      } else {
        initialPositions.set(i, { x: 0, y });
      }
      return y;
    },
    opacity: (i, el) => {
      const pos = initialPositions.get(i);
      return pos ? 0 : 1;
    },
    duration: CONFIG.ANIMATION_DURATION,
    ease: "power2.out",
  }, "+=1");

  // Animate featuredImage: opacity only, at the same time as images animation
  tl.to(featuredImage, {
    opacity: 1,
    duration: CONFIG.ANIMATION_DURATION,
    ease: "power2.out"
  }, "<");

  // Fading out subtitle, title, and description together, with subtitle moving up
  if(!isLastSlide) {
    tl.to([textContainer, featuredImage], {
      opacity: 0,
      duration: CONFIG.ANIMATION_DURATION,
      ease: "power2.inOut",
    });
    
    slideToNext(tl, slide);
  }

  return tl;
}

export function animateVideoText(slide: HTMLElement, isLastSlide: boolean) {
  resetSlide(slide);
  const tl = gsap.timeline();
  const option = slide.dataset.animOption;
  const keepTextUntilEnd = slide.dataset.showTextUntilVideoEnds === "true";

  const textContainer = getElement('[data-slide-text-container]', slide);
  const videoMuteUnmute = getElement('[data-slide-mute-unmute]', slide)
  const videoEl = getElement<HTMLVideoElement>('[data-slide-video-element]', slide);

  let textVideoVisibilityDuration = CONFIG.TEXT_VIDEO_VISIBILITY_DURATION
  if(keepTextUntilEnd && videoEl) {
    textVideoVisibilityDuration = videoEl.duration
  }

  if (!videoEl) return tl;

  if (option === "scale-in") {
    tl.to([textContainer, videoEl, videoMuteUnmute], {
      opacity: (i, el) => (el === textContainer ? 1 : 1),
      scale: (i, el) => (el === videoEl ? 1 : 1),
      duration: CONFIG.ANIMATION_DURATION,
      ease: "power2.out"
    });
    tl.to(textContainer, { opacity: 0, duration: CONFIG.ANIMATION_DURATION }, `+=${textVideoVisibilityDuration}`);
  } else {
    tl.to([videoEl, textContainer, videoMuteUnmute], {
      opacity: 1,
      ease: "power2.out"
    });
    tl.to(textContainer, { opacity: 0, duration: CONFIG.ANIMATION_DURATION }, `+=${textVideoVisibilityDuration}`);
  }

  if (isLastSlide) {
    // Last slide → loop video indefinitely
    videoEl.muted = true;
    videoEl.loop = true;
    videoEl.playsInline = true;
    videoEl.play().catch(err => console.warn("Autoplay failed:", err));
  } else {
    // Normal slide → fade out on ended, then go next
    videoEl.onended = null;
    videoEl.onended = () => {
      gsap.to(videoEl, {
        opacity: 0,
        duration: CONFIG.ANIMATION_DURATION,
        onComplete: () => {
          slide.closest(SELECTOR_SECTION_CONTAINER)?.dispatchEvent(
            new CustomEvent("slideNext")
          );
        }
      });
    };
    videoEl.play().catch(err => console.warn("Autoplay failed:", err));
  }

  return tl;
}

export function animateTextOutward(slide: HTMLElement, isLastSlide: boolean) {
  resetSlide(slide);
  const tl = gsap.timeline();

  const subtitle = getElement('[data-slide-subtitle]', slide);
  const title = getElement('[data-slide-title]', slide);
  const description = getElement('[data-slide-description]', slide);

  tl.to([title, description], {
    opacity: 1,
    ease: "power2.out",
    duration: CONFIG.ANIMATION_DURATION,
    delay: CONFIG.ANIMATION_DELAY,
  });

  const bp: "desktop" | "mobile" = responsiveMediaAbove("medium") ? "desktop" : "mobile";
  const wrapper = slide.querySelector(
    bp === "desktop"
      ? "[data-outward-image-desktop]"
      : "[data-outward-image-mobile]"
  );
  const images = wrapper?.querySelectorAll<HTMLElement>("[data-slide-anim-img]") ?? [];

  const parentBounds = slide.getBoundingClientRect();
  
  /**
   * Calculate final position for an image element based on configuration
   * Supports different positioning strategies for mobile vs desktop
   * 
   * @param element - The HTML element to position
   * @param config - Position configuration object
   * @param parentBounds - Parent container bounds
   * @returns Object with x and y transform values
   */
  const calculatePosition = (element: HTMLElement, config: any, parentBounds: DOMRect) => {
    const elRect = element.getBoundingClientRect();
    
    if (bp === "mobile") {
      // Mobile: Use static pixel movement from current position
      // This approach is completely independent of container size or screen dimensions
      return {
        x: (() => {
          if (config.leftPct !== undefined) {
            // Calculate target left position based on container width percentage
            const targetLeft = (parentBounds.width * config.leftPct) / 100;
            return targetLeft - (elRect.left - parentBounds.left);
          } else if (config.rightPct !== undefined) {
            // Calculate target right position based on container width percentage
            const targetRight = parentBounds.width - (parentBounds.width * config.rightPct) / 100;
            return targetRight - (elRect.right - parentBounds.left);
          }
          return 0;
        })(),
        y: (() => {
          if (config.moveY !== undefined) {
            // Simply move by the static pixel amount from current position
            // Positive values move down, negative values move up
            return config.moveY;
          }
          return 0;
        })()
      };
    } else {
      // Desktop: Keep original percentage-based scaling behavior for backwards compatibility
      const elBounds = element.getBoundingClientRect();
      return {
        x: (() => {
          if (config.leftPct !== undefined) {
            const targetX = (parentBounds.width * config.leftPct) / 100 - (elBounds.left - parentBounds.left);
            return targetX;
          } else if (config.rightPct !== undefined) {
            const targetX = (parentBounds.width * (1 - config.rightPct / 100)) - (elBounds.right - parentBounds.left);
            return targetX;
          }
          return 0;
        })(),
        y: (() => {
          if (config.topPct !== undefined) {
            const targetY = (parentBounds.height * config.topPct) / 100 - (elBounds.top - parentBounds.top);
            return targetY;
          } else if (config.bottomPct !== undefined) {
            const targetY = (parentBounds.height * (1 - config.bottomPct / 100)) - (elBounds.bottom - parentBounds.top);
            return targetY;
          }
          return 0;
        })()
      };
    }
  };

  // Animate images to their final positions
  // Mobile: Uses absolute pixel positioning for consistent placement across different screen heights
  // Desktop: Uses percentage-based positioning for responsive scaling
  tl.to(images,
    {
      x: (i, el) => {
        const role = el.getAttribute("data-role") as keyof typeof OUTWARD_TARGETS[typeof bp];
        const cfg = OUTWARD_TARGETS[bp][role];
        if (!cfg) return 0;

        const position = calculatePosition(el as HTMLElement, cfg, parentBounds);
        return position.x;
      },
      y: (i, el) => {
        const role = el.getAttribute("data-role") as keyof typeof OUTWARD_TARGETS[typeof bp];
        const cfg = OUTWARD_TARGETS[bp][role];
        if (!cfg) return 0;

        const position = calculatePosition(el as HTMLElement, cfg, parentBounds);
        return position.y;
      },
      width: (i, el) => {
        const role = el.getAttribute("data-role") as keyof typeof OUTWARD_TARGETS[typeof bp];
        const cfg = OUTWARD_TARGETS[bp][role];
        return cfg && cfg.widthPx !== undefined
          ? cfg.widthPx
          : (el as HTMLElement).offsetWidth;
      },
      opacity: 1,
      duration: CONFIG.ANIMATION_DURATION,
      ease: "power2.out",
    }
  );

  if (!isLastSlide) slideToNext(tl, slide);

  return tl;
}